import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
    User, Mail, Phone, Shield, Users, Loader2, Save, 
    KeyRound, AlertTriangle, CheckCircle2, Percent, X 
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ROLE_LABELS = {
    admin_full: { label: 'Full Admin', color: 'bg-red-100 text-red-700' },
    admin: { label: 'Admin', color: 'bg-orange-100 text-orange-700' },
    vp: { label: 'VP', color: 'bg-blue-100 text-blue-700' },
    apporteur: { label: 'Apporteur', color: 'bg-green-100 text-green-700' },
    comptable: { label: 'Comptable', color: 'bg-purple-100 text-purple-700' }
};

export function EditUserDialog({ 
    open, 
    onOpenChange, 
    user, 
    vps = [], 
    teams = [],
    allUsers = [],  // Tous les utilisateurs (pour assigner au comptable)
    onSuccess 
}) {
    const [loading, setLoading] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        role: '',
        vp_id: '',
        team_ids: [],
        is_active: true,
        is_admin: false,  // Flag accès admin
        assigned_accountant_user_id: '',  // Pour comptable
        commission_rates: {
            easy_power: { value: '' },
            chantier: { value: '' }
        }
    });

    useEffect(() => {
        if (user) {
            // Support pour la rétrocompatibilité : team_id -> team_ids
            const teamIds = user.team_ids || (user.team_id ? [user.team_id] : []);
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                phone: user.phone || '',
                role: user.role || 'apporteur',
                vp_id: user.vp_id || '',
                team_ids: teamIds,
                is_active: user.is_active !== false,
                is_admin: user.is_admin || false,
                assigned_accountant_user_id: user.assigned_accountant_user_id || '',
                commission_rates: {
                    easy_power: { value: user.commission_rates?.easy_power?.value?.toString() || '' },
                    chantier: { value: user.commission_rates?.chantier?.value?.toString() || '' }
                }
            });
        }
    }, [user]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCommissionChange = (type, value) => {
        setFormData(prev => ({
            ...prev,
            commission_rates: {
                ...prev.commission_rates,
                [type]: { value }
            }
        }));
    };

    const handleTeamToggle = (teamId) => {
        setFormData(prev => {
            const currentTeams = prev.team_ids || [];
            if (currentTeams.includes(teamId)) {
                return { ...prev, team_ids: currentTeams.filter(id => id !== teamId) };
            } else {
                return { ...prev, team_ids: [...currentTeams, teamId] };
            }
        });
    };

    const handleRemoveTeam = (teamId) => {
        setFormData(prev => ({
            ...prev,
            team_ids: (prev.team_ids || []).filter(id => id !== teamId)
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const updates = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone,
                role: formData.role,
                vp_id: formData.vp_id || null,
                team_ids: formData.team_ids || [],
                // Pour rétrocompatibilité, définir aussi team_id si une seule équipe
                team_id: formData.team_ids?.length === 1 ? formData.team_ids[0] : null,
                is_active: formData.is_active
            };

            // Pour comptable: ajouter l'utilisateur assigné
            if (formData.role === 'comptable') {
                updates.assigned_accountant_user_id = formData.assigned_accountant_user_id || null;
            }

            // Ajouter les commissions UNIQUEMENT pour le VP (l'apporteur n'a plus de barème dans sa fiche)
            if (formData.role === 'vp') {
                updates.commission_rates = {
                    easy_power: { 
                        value: parseFloat(formData.commission_rates.easy_power.value) || 0,
                        is_active: true
                    },
                    chantier: { 
                        value: parseFloat(formData.commission_rates.chantier.value) || 0,
                        is_active: true
                    }
                };
            }

            await api.put(`/admin/users/${user.id}`, updates);
            toast.success('Utilisateur mis à jour');
            onSuccess?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!window.confirm(`Envoyer un email de réinitialisation de mot de passe à ${user.email} ?`)) {
            return;
        }

        setResettingPassword(true);
        try {
            const res = await api.post(`/admin/users/${user.id}/reset-password`);
            if (res.data.email_sent) {
                toast.success(`Email de réinitialisation envoyé à ${user.email}`);
            } else {
                toast.warning('Token créé mais email non envoyé (SMTP non configuré)');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
        } finally {
            setResettingPassword(false);
        }
    };

    // Obtenir les noms des équipes sélectionnées
    const getTeamName = (teamId) => {
        const team = teams.find(t => t.id === teamId);
        return team?.name || 'Équipe inconnue';
    };

    if (!user) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Modifier l'utilisateur
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {user.email}
                        <Badge className={ROLE_LABELS[user.role]?.color || 'bg-slate-100'}>
                            {ROLE_LABELS[user.role]?.label || user.role}
                        </Badge>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Informations de base */}
                    <div className="space-y-4">
                        <h4 className="font-medium flex items-center gap-2 text-sm text-slate-700">
                            <User className="w-4 h-4" />
                            Informations personnelles
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">Prénom</Label>
                                <Input
                                    id="first_name"
                                    value={formData.first_name}
                                    onChange={(e) => handleChange('first_name', e.target.value)}
                                    placeholder="Prénom"
                                    data-testid="edit-user-first-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last_name">Nom</Label>
                                <Input
                                    id="last_name"
                                    value={formData.last_name}
                                    onChange={(e) => handleChange('last_name', e.target.value)}
                                    placeholder="Nom"
                                    data-testid="edit-user-last-name"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phone">Téléphone</Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    placeholder="+33 6 12 34 56 78"
                                    className="pl-10"
                                    data-testid="edit-user-phone"
                                />
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Rôle et équipes */}
                    <div className="space-y-4">
                        <h4 className="font-medium flex items-center gap-2 text-sm text-slate-700">
                            <Shield className="w-4 h-4" />
                            Rôle et équipes
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Rôle</Label>
                                <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                                    <SelectTrigger data-testid="edit-user-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin_full">Full Admin</SelectItem>
                                        <SelectItem value="admin">Admin</SelectItem>
                                        <SelectItem value="vp">VP</SelectItem>
                                        <SelectItem value="apporteur">Apporteur</SelectItem>
                                        <SelectItem value="comptable">Comptable</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {formData.role === 'apporteur' && (
                                <div className="space-y-2">
                                    <Label>VP Rattaché</Label>
                                    <Select value={formData.vp_id || "none"} onValueChange={(v) => handleChange('vp_id', v === "none" ? '' : v)}>
                                        <SelectTrigger data-testid="edit-user-vp">
                                            <SelectValue placeholder="Choisir un VP" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Aucun (Apporteur libre)</SelectItem>
                                            {vps.map(vp => (
                                                <SelectItem key={vp.id} value={vp.id}>
                                                    {vp.first_name} {vp.last_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {(!formData.vp_id || formData.vp_id === 'none') && (
                                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                                            ⚠️ <strong>Apporteur libre</strong> : Travaille directement pour Valnexo/AT Service. 
                                            Sa commission est prélevée sur le total, le reste est partagé 50/50.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Comptable: Sélection de l'utilisateur à suivre */}
                        {formData.role === 'comptable' && (
                            <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <Label className="flex items-center gap-2 text-purple-800">
                                    <User className="w-4 h-4" />
                                    Utilisateur assigné (visibilité limitée)
                                </Label>
                                <p className="text-xs text-purple-600 mb-2">
                                    Le comptable ne verra QUE les données de cet utilisateur (prospects, commissions, documents).
                                </p>
                                <Select 
                                    value={formData.assigned_accountant_user_id || "none"} 
                                    onValueChange={(v) => handleChange('assigned_accountant_user_id', v === "none" ? '' : v)}
                                >
                                    <SelectTrigger data-testid="edit-user-assigned-accountant">
                                        <SelectValue placeholder="Sélectionner un utilisateur" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Aucun (accès vide)</SelectItem>
                                        {allUsers
                                            .filter(u => u.role === 'apporteur' || u.role === 'vp')
                                            .filter(u => u.is_active)
                                            .map(u => (
                                                <SelectItem key={u.id} value={u.id}>
                                                    {u.first_name} {u.last_name} ({u.role === 'vp' ? 'VP' : 'Apporteur'})
                                                </SelectItem>
                                            ))
                                        }
                                    </SelectContent>
                                </Select>
                                {!formData.assigned_accountant_user_id && (
                                    <Alert className="bg-amber-50 border-amber-200">
                                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                                        <AlertDescription className="text-amber-700 text-xs">
                                            Sans utilisateur assigné, le comptable n'aura accès à aucune donnée.
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div>
                        )}

                        {/* Sélecteur Multi-Équipes */}
                        {(formData.role === 'vp' || formData.role === 'apporteur') && teams.length > 0 && (
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Équipes assignées
                                    <Badge variant="outline" className="ml-2">{formData.team_ids?.length || 0}</Badge>
                                </Label>
                                
                                {/* Équipes sélectionnées */}
                                {formData.team_ids?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                        {formData.team_ids.map(teamId => (
                                            <Badge 
                                                key={teamId} 
                                                className="bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer flex items-center gap-1 pr-1"
                                                data-testid={`selected-team-${teamId}`}
                                            >
                                                {getTeamName(teamId)}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTeam(teamId)}
                                                    className="ml-1 rounded-full hover:bg-blue-300 p-0.5"
                                                    aria-label={`Retirer ${getTeamName(teamId)}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                {/* Liste des équipes disponibles */}
                                <ScrollArea className="h-[150px] rounded-md border p-3">
                                    <div className="space-y-2">
                                        {teams.map(team => (
                                            <div 
                                                key={team.id} 
                                                className="flex items-center space-x-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                                                onClick={() => handleTeamToggle(team.id)}
                                            >
                                                <Checkbox
                                                    id={`team-${team.id}`}
                                                    checked={formData.team_ids?.includes(team.id)}
                                                    onCheckedChange={() => handleTeamToggle(team.id)}
                                                    data-testid={`team-checkbox-${team.id}`}
                                                />
                                                <label
                                                    htmlFor={`team-${team.id}`}
                                                    className="flex-1 text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                >
                                                    {team.name}
                                                </label>
                                            </div>
                                        ))}
                                        {teams.length === 0 && (
                                            <p className="text-sm text-slate-500 text-center py-4">
                                                Aucune équipe disponible
                                            </p>
                                        )}
                                    </div>
                                </ScrollArea>
                            </div>
                        )}
                    </div>

                    {/* Commissions (VP uniquement) */}
                    {formData.role === 'vp' && (
                        <>
                            <Separator />
                            <div className="space-y-4">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-slate-700">
                                    <Percent className="w-4 h-4" />
                                    Barèmes de commission
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                                            Easy Power (%)
                                        </Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            value={formData.commission_rates.easy_power.value}
                                            onChange={(e) => handleCommissionChange('easy_power', e.target.value)}
                                            placeholder="Ex: 15"
                                            data-testid="edit-user-commission-ep"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                                            Chantier (%)
                                        </Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            value={formData.commission_rates.chantier.value}
                                            onChange={(e) => handleCommissionChange('chantier', e.target.value)}
                                            placeholder="Ex: 10"
                                            data-testid="edit-user-commission-ch"
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Commission apporteur : gérée sur la page Équipe */}
                    {formData.role === 'apporteur' && (
                        <>
                            <Separator />
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-testid="edit-user-apporteur-commission-info">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-blue-800">
                                    <Percent className="w-4 h-4" />
                                    Commission de l'apporteur
                                </h4>
                                <p className="text-xs text-blue-600 mt-1">
                                    Le pourcentage de cet apporteur se règle dans son équipe
                                    (page <span className="font-medium">Équipe → « Modifier commission »</span>),
                                    pour Easy Power et Chantier. C'est l'unique source utilisée dans les calculs.
                                </p>
                            </div>
                        </>
                    )}


                    <Separator />

                    {/* Statut */}
                    <div className="space-y-4">
                        <h4 className="font-medium flex items-center gap-2 text-sm text-slate-700">
                            Statut du compte
                        </h4>
                        <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50">
                            <div>
                                <Label htmlFor="is_active" className="font-medium">Compte actif</Label>
                                <p className="text-xs text-slate-500">
                                    Un compte désactivé ne peut plus se connecter
                                </p>
                            </div>
                            <Switch
                                id="is_active"
                                checked={formData.is_active}
                                onCheckedChange={(v) => handleChange('is_active', v)}
                                data-testid="edit-user-active-switch"
                            />
                        </div>
                        
                        {/* Accès Admin - Visible uniquement pour les non-admin_full */}
                        {formData.role !== 'admin_full' && (
                            <div className="flex items-center justify-between p-4 rounded-lg border bg-amber-50 border-amber-200">
                                <div>
                                    <Label htmlFor="is_admin" className="font-medium text-amber-800">
                                        Accès Admin
                                    </Label>
                                    <p className="text-xs text-amber-700">
                                        Donne les droits admin tout en gardant le rôle actuel ({ROLE_LABELS[formData.role]?.label || formData.role})
                                    </p>
                                </div>
                                <Switch
                                    id="is_admin"
                                    checked={formData.is_admin}
                                    onCheckedChange={async (v) => {
                                        handleChange('is_admin', v);
                                        // Mettre à jour immédiatement via l'API
                                        try {
                                            await api.put(`/admin/users/${user.id}/admin-access`, { is_admin: v });
                                            toast.success(v ? 'Accès admin activé' : 'Accès admin désactivé');
                                        } catch (err) {
                                            toast.error('Erreur lors de la modification');
                                            handleChange('is_admin', !v); // Revert
                                        }
                                    }}
                                    data-testid="edit-user-admin-switch"
                                />
                            </div>
                        )}
                    </div>

                    <Separator />

                    {/* Réinitialisation mot de passe */}
                    <div className="space-y-4">
                        <h4 className="font-medium flex items-center gap-2 text-sm text-slate-700">
                            <KeyRound className="w-4 h-4" />
                            Mot de passe
                        </h4>
                        <Alert className="bg-amber-50 border-amber-200">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <AlertTitle className="text-amber-800">Réinitialiser le mot de passe</AlertTitle>
                            <AlertDescription className="text-amber-700 text-sm">
                                L'utilisateur recevra un email avec un lien pour définir un nouveau mot de passe (valide 24h).
                            </AlertDescription>
                        </Alert>
                        <Button
                            variant="outline"
                            onClick={handleResetPassword}
                            disabled={resettingPassword}
                            className="w-full"
                            data-testid="reset-password-btn"
                        >
                            {resettingPassword ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <KeyRound className="w-4 h-4 mr-2" />
                                    Envoyer le lien de réinitialisation
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-user-cancel">
                        Annuler
                    </Button>
                    <Button onClick={handleSave} disabled={loading} data-testid="edit-user-save">
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Enregistrement...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                Enregistrer
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
